import { FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { faClock, faRotate } from "@fortawesome/pro-regular-svg-icons";

import { isFinishedRunNote } from "@signalist/shared-types";

import { translateRequestError } from "@/utils/errors";
import { showGlobalToast } from "@/utils/showGlobalToast";

import { Icon } from "@/components";
import { Spinner } from "@/components/Spinner/Spinner";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { ListProcessingStatus, ListSourceType } from "@/enum/list.enum";

import { useRerunListMutation } from "@/pages/Private/redux/list/list-rerun.api";

import { List } from "../schema/list";
import { useListConfigurationStatus } from "../utils/useListConfigurationStatus";
import { getTrackerStatusLabel } from "../utils/trackerStatusLabel";
import { rerunListGate, RerunListGate } from "../utils/rerunListGate";

interface RerunListButtonProps {
	list: List;
}

export const RerunListButton: FunctionComponent<RerunListButtonProps> = ({ list }) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`list.rerun.${key}`), [t]);
	const [rerunList] = useRerunListMutation();
	const [isRerunning, setIsRerunning] = useState(false);

	// The lean `list` carries the lead counts the full list omits, so a hand-populated upload list
	// is told apart from an unconfigured one and gets its own disabled reason (PREC-4118).
	const config = useListConfigurationStatus(list.id, list);
	// PREC-4994: the Sources menu's failed-Maps hint reads the same gate, so this control and that
	// hint cannot disagree about whether the list can be re-run right now.
	const gate = rerunListGate(list, config);
	const isProcessing = gate === "running";
	const isDisabled = gate !== "available" && !isProcessing;

	const handleRerun = useCallback(async () => {
		if (isDisabled || isProcessing || isRerunning) {
			return;
		}

		setIsRerunning(true);

		try {
			await rerunList(list.id).unwrap();
		} catch (error) {
			showGlobalToast(translateRequestError(error));
		} finally {
			setIsRerunning(false);
		}
	}, [isDisabled, isProcessing, isRerunning, list.id, rerunList]);

	if (isProcessing) {
		// TRACKER lists have no lead-fill goal, so they show what they're monitoring rather than a
		// (permanently 0%) progress percentage — matching the overview status chip (PREC-3601).
		const message =
			list.sourceType === ListSourceType.TRACKER
				? getTrackerStatusLabel(t, list.trackerOrganizationCount)
				: list.processingProgressPercent != null
					? `${list.processingProgressPercent}%`
					: (list.processingStatusMessage ?? ts("running"));

		return (
			<div
				className="flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-status-running-light text-status-running text-xs font-medium"
				data-testid="rerun-list-button-running"
			>
				<Icon className="w-3.5 h-3.5" icon={faClock} />
				<span className="truncate max-w-[260px]">{message}</span>
			</div>
		);
	}

	const button = (
		<button
			className="flex items-center gap-1 h-8 px-2 rounded-md border border-border-light text-xs font-medium text-text-meta hover:text-text hover:border-blue-action disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
			data-testid="rerun-list-button"
			disabled={isDisabled || isRerunning}
			type="button"
			onClick={handleRerun}
		>
			{isRerunning ? (
				<Spinner color="#6B7280" />
			) : (
				<Icon className="w-3 h-3 text-text-meta" icon={faRotate} />
			)}
			<span>{ts("rerun")}</span>
		</button>
	);

	const control = isDisabled ? (
		<Tooltip content={disabledReason(gate, config, t, ts)}>{button}</Tooltip>
	) : (
		button
	);

	// PREC-4121: an idle list may carry the reason its last run ended ("No source to process",
	// "Run stopped …"). It sits beside the re-run control, which is the action it asks for.
	// PREC-4470: so may a finished one, when the message is a run-end note (not a stale label).
	// PREC-4829: the note is cut at 260px, and the provider-outage sentence ends in what the user
	// most needs ("nothing to do on your side"), so hovering it shows the whole sentence.
	const endedNote =
		(list.processingStatus === ListProcessingStatus.IDLE && list.processingStatusMessage) ||
		(list.processingStatus === ListProcessingStatus.DONE &&
			isFinishedRunNote(list.processingStatusMessage)) ? (
			<span
				className="truncate max-w-[260px] text-xs text-status-warning"
				data-testid="rerun-list-button-note"
				title={list.processingStatusMessage ?? undefined}
			>
				{list.processingStatusMessage}
			</span>
		) : null;

	if (!endedNote) {
		return control;
	}

	return (
		<div className="flex items-center gap-2">
			{endedNote}
			{control}
		</div>
	);
};

const disabledReason = (
	gate: RerunListGate,
	config: { sourceManualOnly?: boolean; sourceMissing?: boolean },
	t: (key: string) => string,
	ts: (key: string) => string
): string => {
	if (gate === "archived") {
		return ts("disabledArchived");
	}

	if (gate === "unconfigured") {
		return config.sourceManualOnly ? t("list.noFile.cannotRun") : ts("disabledSourceMissing");
	}

	return ts("disabledStopping");
};
